import { DialogComponent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #activeStep = "items";
  #deliveryComplete = false;
  #deliveryRecap = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener("click", this.#handleStepClick);
    this.addEventListener("jsi:delivery-state", this.#handleDeliveryState);
    this.addEventListener(
      "jsi:show-delivery-step",
      this.#handleShowDeliveryStep,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener("click", this.#handleStepClick);
    this.removeEventListener("jsi:delivery-state", this.#handleDeliveryState);
    this.removeEventListener(
      "jsi:show-delivery-step",
      this.#handleShowDeliveryStep,
    );
  }

  #handleCartAdd = () => {
    if (this.hasAttribute("auto-open")) {
      this.#activeStep = "items";
      this.showDialog();
      requestAnimationFrame(this.#syncSteps);
    }
  };

  #handleStepClick = (event) => {
    const target = event.target.closest(
      "[data-cart-step-target], [data-cart-step-next]",
    );
    if (!target || target.disabled) return;

    const requestedStep =
      target.dataset.cartStepTarget || target.dataset.cartStepNext;
    this.goToStep(requestedStep);
  };

  #handleDeliveryState = (event) => {
    this.#deliveryComplete = Boolean(event.detail?.complete);
    this.#deliveryRecap = event.detail || null;
    this.#syncSteps();
  };

  #handleShowDeliveryStep = () => {
    this.goToStep("delivery");
  };

  #requiresDelivery = () => {
    const flags = this.querySelector("#jsi-cart-flags");
    return (
      flags?.dataset.requiresDelivery === "true" ||
      flags?.dataset.hasDunk === "true"
    );
  };

  #syncSteps = () => {
    const hasCart = Boolean(this.querySelector(".cart-form"));
    if (!hasCart) return;

    const requiresDelivery = this.#requiresDelivery();
    const deliveryPanel = this.querySelector('[data-cart-step="delivery"]');

    if (
      this.#activeStep === "delivery" &&
      (!requiresDelivery || !deliveryPanel)
    ) {
      this.#activeStep = requiresDelivery ? "items" : "summary";
    }
    if (
      this.#activeStep === "summary" &&
      requiresDelivery &&
      !this.#deliveryComplete
    ) {
      this.#activeStep = deliveryPanel ? "delivery" : "items";
    }

    this.querySelectorAll("[data-cart-step]").forEach((panel) => {
      panel.hidden = panel.dataset.cartStep !== this.#activeStep;
    });

    const stepOrder = requiresDelivery
      ? ["items", "delivery", "summary"]
      : ["items", "summary"];
    const activeIndex = stepOrder.indexOf(this.#activeStep);

    this.querySelectorAll("[data-cart-step-target]").forEach((button) => {
      const step = button.dataset.cartStepTarget;
      const index = stepOrder.indexOf(step);
      const isAvailable = index !== -1;
      const canVisit =
        step === "items" ||
        step === "delivery" ||
        (step === "summary" && (!requiresDelivery || this.#deliveryComplete));

      button.hidden = !isAvailable;
      button.disabled = !isAvailable || !canVisit;
      button.classList.toggle("is-active", step === this.#activeStep);
      button.classList.toggle(
        "is-complete",
        isAvailable && index < activeIndex,
      );

      if (step === this.#activeStep)
        button.setAttribute("aria-current", "step");
      else button.removeAttribute("aria-current");
    });

    const deliveryLine = this.querySelector(
      ".cart-drawer__step-line--delivery",
    );
    if (deliveryLine) deliveryLine.hidden = !requiresDelivery;

    const summaryNumber = this.querySelector("[data-cart-summary-step-number]");
    if (summaryNumber) summaryNumber.textContent = requiresDelivery ? "3" : "2";

    const summaryEyebrow = this.querySelector("[data-cart-summary-eyebrow]");
    if (summaryEyebrow)
      summaryEyebrow.textContent = requiresDelivery ? "Trin 3" : "Trin 2";

    const itemsButton = this.querySelector("[data-cart-items-next]");
    if (itemsButton)
      itemsButton.dataset.cartStepNext = requiresDelivery
        ? "delivery"
        : "summary";

    const itemsLabel = this.querySelector("[data-cart-items-continue-label]");
    if (itemsLabel)
      itemsLabel.textContent = requiresDelivery
        ? "Vælg levering"
        : "Se total og betal";

    const recap = this.querySelector("[data-cart-delivery-recap]");
    if (recap) {
      recap.hidden = !this.#deliveryComplete;
      const recapTitle = recap.querySelector(
        "[data-cart-delivery-recap-title]",
      );
      const recapDetail = recap.querySelector(
        "[data-cart-delivery-recap-detail]",
      );
      if (recapTitle)
        recapTitle.textContent = this.#deliveryRecap?.label || "Levering valgt";
      if (recapDetail)
        recapDetail.textContent = this.#deliveryRecap?.summary || "";
    }

    this.#syncEstimatedTotal();
  };

  #syncEstimatedTotal = () => {
    const flags = this.querySelector("#jsi-cart-flags");
    const totalValue = this.querySelector(".cart__total-value");
    const totalLabel = this.querySelector(".cart__total-label");
    if (!flags || !totalValue || !totalLabel) return;

    const baseTotalMinor = Number.parseInt(
      flags.dataset.baseTotalMinor || "",
      10,
    );
    if (!Number.isFinite(baseTotalMinor)) return;

    const deliveryPriceKnown =
      this.#deliveryComplete && this.#deliveryRecap?.priceKnown === true;
    const deliveryTotalMinor = deliveryPriceKnown
      ? Number(this.#deliveryRecap?.deliveryPriceMinor || 0)
      : 0;
    const expectedTotalMinor = baseTotalMinor + deliveryTotalMinor;
    const currency = flags.dataset.currency || "DKK";

    if (!totalLabel.dataset.baseLabel) {
      totalLabel.dataset.baseLabel = totalLabel.textContent.trim();
    }

    totalValue.textContent = this.#formatMoney(expectedTotalMinor, currency);
    totalLabel.textContent = deliveryPriceKnown
      ? "Forventet total inkl. levering"
      : totalLabel.dataset.baseLabel;

    const breakdown = this.querySelector("[data-cart-total-breakdown]");
    if (breakdown) {
      breakdown.hidden = !deliveryPriceKnown;
      const baseValue = breakdown.querySelector("[data-cart-base-total]");
      const deliveryValue = breakdown.querySelector(
        "[data-cart-delivery-total]",
      );
      if (baseValue)
        baseValue.textContent = this.#formatMoney(baseTotalMinor, currency);
      if (deliveryValue)
        deliveryValue.textContent = this.#formatMoney(
          deliveryTotalMinor,
          currency,
        );
    }

    const pricingNote = this.querySelector("[data-cart-pricing-note-text]");
    if (pricingNote) {
      pricingNote.textContent = deliveryPriceKnown
        ? "Leveringsprisen er medregnet ovenfor og bekræftes endeligt i checkout."
        : this.#deliveryRecap?.method === "gls"
          ? "Prisen for pakkepost beregnes i checkout og er ikke med i beløbet endnu."
          : "Leveringsprisen lægges til det forventede totalbeløb, når dit valg er beregnet.";
    }
  };

  #formatMoney = (amountMinor, currency) => {
    const locale = document.documentElement.lang || "da-DK";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "code",
    }).format(amountMinor / 100);
  };

  goToStep(requestedStep) {
    const requiresDelivery = this.#requiresDelivery();
    let nextStep = requestedStep;

    if (nextStep === "delivery" && !requiresDelivery) nextStep = "summary";
    if (nextStep === "summary" && requiresDelivery && !this.#deliveryComplete)
      nextStep = "delivery";
    if (
      nextStep === "delivery" &&
      !this.querySelector('[data-cart-step="delivery"]')
    )
      nextStep = "items";

    this.#activeStep = nextStep;
    this.#syncSteps();

    const panel = this.querySelector(`[data-cart-step="${nextStep}"]`);
    const scrollContainer =
      panel?.querySelector(":scope > .cart-drawer__items") || panel;
    scrollContainer?.scrollTo?.({ top: 0, behavior: "auto" });
    panel?.querySelector("h2, h3")?.focus?.({ preventScroll: true });
  }

  open() {
    this.showDialog();
    requestAnimationFrame(this.#syncSteps);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
